import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { FileText, Copy, Check, Send } from "lucide-react";

export const PlanRequestModal = ({
  open,
  onClose,
  requestText,
  onSaveAndApply,
  onSend,
}) => {
  const [editedText, setEditedText] = useState(requestText || "");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setEditedText(requestText || "");
  }, [requestText, open]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback ignorado
    }
  };

  const handleApplyToChat = () => {
    if (onSaveAndApply) {
      onSaveAndApply(editedText);
    }
    onClose();
  };

  const handleSendDirectly = () => {
    if (onSaveAndApply) {
      onSaveAndApply(editedText);
    }
    if (onSend) {
      onSend(editedText);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent className="max-w-4xl max-h-[88vh] flex flex-col p-0 gap-0 overflow-hidden bg-slate-900 border-slate-700 text-slate-100 shadow-2xl">
        <DialogHeader className="px-6 py-4 border-b border-slate-800 bg-slate-900/90 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-400">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-slate-100">
                Solicitud de Planificación de Producción
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-400 mt-0.5">
                Revisa y ajusta el contexto preparado antes de enviarlo al Creative Director.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 p-6 overflow-y-auto bg-slate-950/60 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
              Contenido de la solicitud (Editable)
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400">Copiado</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span>Copiar texto</span>
                </>
              )}
            </button>
          </div>
          <textarea
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            rows={18}
            className="w-full flex-1 min-h-[320px] p-4 font-mono text-xs leading-relaxed bg-slate-900/90 border border-slate-700/80 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none selection:bg-indigo-500/30"
            placeholder="Texto de la solicitud..."
          />
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-800 bg-slate-900/90 flex-shrink-0 flex items-center justify-between sm:justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
          >
            Cerrar sin guardar
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleApplyToChat}
              className="px-4 py-2 text-xs font-medium text-indigo-300 hover:text-indigo-200 bg-indigo-950/40 hover:bg-indigo-900/50 border border-indigo-700/50 rounded-lg transition-colors"
            >
              Aplicar al chat
            </button>
            <button
              type="button"
              onClick={handleSendDirectly}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg shadow-sm transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
              Enviar solicitud
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlanRequestModal;
